import classNames from 'classnames';
import { useTranslations } from 'next-intl';

const POINT_KEYS = ['point1', 'point2', 'point3'];

// Memory-keeping use case — cross-device sync.
export const Section31 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            {t('pages.useCases.memoryKeeping.anywhere.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.useCases.memoryKeeping.anywhere.title', {
              br: () => <br />,
            })}
          </h2>
          <p className={styles.lead}>
            {t('pages.useCases.memoryKeeping.anywhere.lead')}
          </p>
          <ul className={styles.list}>
            {POINT_KEYS.map((key) => (
              <li key={key} className={styles.item}>
                <span className={styles.check}>
                  <svg
                    viewBox="0 0 24 24"
                    className={styles.checkGlyph}
                    aria-hidden="true"
                  >
                    <path d="m5 12 5 5L20 7" />
                  </svg>
                </span>
                {t(`pages.useCases.memoryKeeping.anywhere.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        {/* Device mockup */}
        <div className={styles.stage} aria-hidden="true">
          <div className={styles.laptop}>
            <div className={styles.screen}>
              <div className={styles.browserBar}>
                <span className={styles.dotR} />
                <span className={styles.dotY} />
                <span className={styles.dotG} />
                <span className={styles.url}>mindfeed.com/box/family</span>
              </div>
              <div className={styles.screenBody}>
                <div className={styles.sidebar}>
                  <span className={styles.navRowActive} />
                  <span className={styles.navRow} />
                  <span className={styles.navRow} />
                  <span className={styles.navRow} />
                </div>
                <div className={styles.main}>
                  <span className={styles.heading} />
                  <span className={styles.tileActive} />
                  <span className={styles.tile} />
                  <span className={styles.tile} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.syncLine} />
          <div className={styles.phone}>
            <div className={styles.phoneScreen}>
              <div className={styles.phoneTitle}>Family</div>
              <span className={`${styles.phoneTile} ${styles.accentSoft}`} />
              <span className={styles.phoneRow} />
              <span className={styles.phoneRow} />
              <span className={styles.phoneRow} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10',
  copy: 'text-center lg:text-left',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  lead: 'mx-auto mt-6 max-w-[480px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-xl',
  list: 'mx-auto mt-8 flex max-w-[440px] flex-col gap-4 text-left lg:mx-0',
  item: 'flex items-center gap-3 text-[15px] font-medium text-mf-off-black-600 marketing-dark:text-white',
  check: classNames(
    'flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full',
    'bg-[#2BA471]/15 text-[#2BA471]',
    'marketing-dark:bg-[#6ed7a6]/20 marketing-dark:text-[#6ed7a6]',
  ),
  checkGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-[2.4]',
  // Device mockup — dark (ink) frames in both themes; the screens show the
  // light app UI on the light page and the dark app UI on the navy page.
  stage: 'relative mx-auto h-[460px] w-[440px] max-w-full',
  laptop: classNames(
    'absolute left-0 top-5 w-[420px] max-w-full rounded-t-[12px] rounded-b-[3px] p-3 pb-5',
    'bg-mf-off-black-600 shadow-[0_18px_36px_rgba(20,30,60,0.14)]',
    'marketing-dark:bg-[#0e1c3f] marketing-dark:shadow-[0_30px_70px_rgba(2,8,30,0.6)]',
  ),
  screen: classNames(
    'flex aspect-[16/10] flex-col overflow-hidden rounded-md',
    'bg-white marketing-dark:bg-[#0a1430]',
  ),
  browserBar: classNames(
    'flex items-center gap-1.5 border-b px-2.5 py-2',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.03]',
  ),
  dotR: 'h-2 w-2 rounded-full bg-[#f87171]',
  dotY: 'h-2 w-2 rounded-full bg-[#facc15]',
  dotG: 'h-2 w-2 rounded-full bg-[#34d399]',
  url: 'flex-1 text-center text-[10px] text-mf-gray-500 marketing-dark:text-white/40',
  screenBody: 'grid flex-1 grid-cols-[70px_1fr] gap-2.5 p-3',
  sidebar:
    'flex flex-col gap-1.5 rounded-md bg-[#F4F4F4] p-2 marketing-dark:bg-white/[0.03]',
  navRow: 'h-2.5 rounded bg-[#E0E0E0] marketing-dark:bg-white/10',
  // Accent (selected) fills: blue on the light app UI, coral on the dark one.
  navRowActive: 'h-2.5 rounded bg-mf-blue/40 marketing-dark:bg-mf-coral/40',
  main: 'flex flex-col gap-1.5',
  heading: 'mb-1 h-3.5 w-3/5 rounded bg-[#DADADA] marketing-dark:bg-white/15',
  tile: 'h-10 rounded-md bg-[#F0F0F0] marketing-dark:bg-white/[0.06]',
  tileActive: 'h-10 rounded-md bg-mf-blue/20 marketing-dark:bg-mf-coral/25',
  accentSoft: 'bg-mf-blue/20 marketing-dark:bg-mf-coral/25',
  syncLine: classNames(
    'absolute left-[372px] top-1/2 hidden h-0.5 w-14 -translate-y-1/2 lg:block',
    '[background:repeating-linear-gradient(90deg,#2757BA_0_6px,transparent_6px_10px)]',
    'marketing-dark:[background:repeating-linear-gradient(90deg,#7fa8ff_0_6px,transparent_6px_10px)]',
  ),
  phone: classNames(
    'absolute bottom-0 right-3 w-[170px] rounded-[26px] p-2',
    'bg-mf-off-black-600 shadow-[0_18px_36px_rgba(20,30,60,0.14)]',
    'marketing-dark:bg-[#0e1c3f] marketing-dark:shadow-[0_30px_70px_rgba(2,8,30,0.6)]',
  ),
  phoneScreen:
    'aspect-[9/19] overflow-hidden rounded-[20px] bg-white p-3 marketing-dark:bg-[#0a1430]',
  phoneTitle:
    'mb-2.5 text-xs font-bold text-mf-off-black-600 marketing-dark:text-white',
  phoneTile: 'mb-2 block h-16 rounded-[10px]',
  phoneRow:
    'mb-2 block h-9 rounded-[10px] bg-[#F4F4F4] marketing-dark:bg-white/[0.05]',
};
